import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(isoWeek);

export const groupByWeeks = (data: any) => {
  const newData = data?.map((item: any) => {
    const weeks: { [key: number]: number } = {};
    for (const dayData of item.data) {
      const date = dayData?.[0];
      const value = dayData?.[1];
      const dateObj = dayjs(date);
      let weekNumber = dateObj.isoWeek();

      // Adjust week number for December dates in ISO week 1 of the next year
      if (dateObj.month() === 11 && weekNumber === 1) {
        weekNumber = 53;
      }

      if (!weeks[weekNumber]) {
        weeks[weekNumber] = value;
      } else {
        weeks[weekNumber] += value;
      }
    }

    // Convert to array and sort by week number
    const entries = Object.entries(weeks)
      .map(([key, value]) => [Number(key), value] as [number, number])
      .sort((a, b) => a[0] - b[0]);

    return {
      name: item?.name,
      data: entries,
    };
  });
  return newData;
};

export const groupByLastDayWeeks = (data: any) => {
  const newData = data?.map((item: any) => {
    let weeks: { [key: number]: { value: number; date: string } } = {};

    for (let dayData of item.data) {
      const date = dayData?.[0];
      const value = dayData?.[1];
      const weekNumber = dayjs(date).week();

      // Always update the value, effectively keeping the last value for each week
      weeks[weekNumber] = {
        value: value,
        date: date, // Store the date to compare later
      };
    }

    return {
      name: item?.name,
      data: Object.entries(weeks)?.map(([key, weekData]) => [
        Number(key),
        weekData.value,
      ]),
    };
  });
  return newData;
};

export function monthlyToQuarterly(monthlyData: Record<string, number>) {
  return [
    [1, 2, 3].reduce(
      (sum, month) => sum + (monthlyData[month.toString()] || 0),
      0
    ),
    [4, 5, 6].reduce(
      (sum, month) => sum + (monthlyData[month.toString()] || 0),
      0
    ),
    [7, 8, 9].reduce(
      (sum, month) => sum + (monthlyData[month.toString()] || 0),
      0
    ),
    [10, 11, 12].reduce(
      (sum, month) => sum + (monthlyData[month.toString()] || 0),
      0
    ),
  ];
}

export function timeseriesToQuarterly(
  timeseriesData: Array<{ name: string; data: [number, number][] }>
) {
  return timeseriesData.map((series) => {
    // Initialize quarterly sums
    const quarterlyData = {
      1: { sum: 0 }, // Q1 (months 0,1,2)
      2: { sum: 0 }, // Q2 (months 3,4,5)
      3: { sum: 0 }, // Q3 (months 6,7,8)
      4: { sum: 0 }, // Q4 (months 9,10,11)
    };

    // Process each month's data
    series.data.forEach(([timestamp, value], index) => {
      const quarter = Math.floor(index / 3) + 1;
      if (quarter <= 4) {
        // @ts-ignore
        quarterlyData[quarter].sum += value;
      }
    });

    // Convert to final format
    const formattedData = Object.entries(quarterlyData).map(
      ([quarter, data]) => [Number(quarter), data.sum]
    );

    return {
      name: series.name,
      data: formattedData,
    };
  });
}

export function timeseriesToQuarterlyByLastMonth(
  timeseriesData: Array<{ name: string; data: [number, number][] }>
) {
  return timeseriesData.map((series) => {
    // Initialize quarterly values
    const quarterlyData = {
      1: { value: null }, // Q1 (Jan-Mar)
      2: { value: null }, // Q2 (Apr-Jun)
      3: { value: null }, // Q3 (Jul-Sep)
      4: { value: null }, // Q4 (Oct-Dec)
    };

    // Process each month's data
    series.data.forEach(([timestamp, value], index) => {
      const quarter = Math.floor(index / 3) + 1;
      if (quarter <= 4) {
        // Always update with the latest available value for the quarter
        // @ts-ignore
        quarterlyData[quarter].value = value;
      }
    });

    // Convert to final format
    const formattedData = Object.entries(quarterlyData).map(
      ([quarter, data]) => [Number(quarter), data.value ?? 0]
    );

    return {
      name: series.name,
      data: formattedData,
    };
  });
}

export const mergeMultipleMaterialVolume = (...arrays: any[]) => {
  // Create a map to store volumes by material ID
  const materialMap = new Map();

  // Helper function to add materials to the map
  const addToMap = (items: any) => {
    items.forEach((item: any) => {
      const materialId = item.material.toString(); // Convert ObjectId to string for comparison
      if (materialMap.has(materialId)) {
        // If material exists, add volumes
        materialMap.set(materialId, {
          material: item.material, // Keep the original material object/id
          volume: materialMap.get(materialId).volume + item.volume,
        });
      } else {
        // If material doesn't exist, add it
        materialMap.set(materialId, {
          material: item.material,
          volume: item.volume,
        });
      }
    });
  };

  // Process all arrays
  arrays.forEach((array) => addToMap(array));

  // Convert map back to array
  return Array.from(materialMap.values());
};

export const mergeMultipleMaterialRevenue = (...arrays: any[]) => {
  // Create a map to store revenues by material ID
  const materialMap = new Map();

  // Helper function to add materials to the map
  const addToMap = (items: any) => {
    items.forEach((item: any) => {
      const materialId = item.material.toString(); // Convert ObjectId to string for comparison
      if (materialMap.has(materialId)) {
        // If material exists, add revenues
        materialMap.set(materialId, {
          material: item.material, // Keep the original material object/id
          revenue: materialMap.get(materialId).revenue + item.revenue,
        });
      } else {
        // If material doesn't exist, add it
        materialMap.set(materialId, {
          material: item.material,
          revenue: item.revenue,
        });
      }
    });
  };

  // Process all arrays
  arrays.forEach((array) => addToMap(array));

  // Convert map back to array
  return Array.from(materialMap.values());
};

export const mergeMultipleMaterialConversion = (...arrays: any[]) => {
  // Create a map to store revenues by material ID
  const materialMap = new Map();

  // Helper function to add materials to the map
  const addToMap = (items: any) => {
    items.forEach((item: any) => {
      const materialId = item.material.toString(); // Convert ObjectId to string for comparison
      if (materialMap.has(materialId)) {
        // If material exists, add revenues
        materialMap.set(materialId, {
          material: item.material, // Keep the original material object/id
          users: materialMap.get(materialId).users + item.users,
        });
      } else {
        // If material doesn't exist, add it
        materialMap.set(materialId, {
          material: item.material,
          users: item.users,
        });
      }
    });
  };

  // Process all arrays
  arrays.forEach((array) => addToMap(array));

  // Convert map back to array
  return Array.from(materialMap.values());
};
